import { ENV } from '@/config/env'
import type {
    AuthMeResponse,
    AuthRefreshResponse,
    EntryVolumeCurrency,
    EntryVolumeGranularity,
    EntryVolumeResponse,
    ErrorEnvelope,
    NonceRequest,
    NonceResponse,
    Operation,
    OperationsListCursor,
    OperationsListResponse,
    OperatorRole,
    OperatorVerifyRequest,
    OperatorVerifyResponse,
    Readiness,
    ReadyResponse,
    RolesByChain,
} from '@/types/api'
import type {
    DocumentSignedUrlResponse,
    GateType,
    LedgerPage,
    PlayerDetail,
    PlayerDetailWire,
    SelfExclusionDuration,
    SelfExclusionLiftReason,
    TierId,
    WalletSanctionsStatus,
} from '@/types/player-detail'
import type {
    AuditEntry,
    AuditLogListFilters,
    AuditLogListResponse,
    AuditLogListWire,
    AuditRecordWire,
} from '@/types/audit-log'
import { mapAuditEntry } from '@/lib/audit-wire'
import type { BetaTester, BetaTesterUpdate } from '@/types/beta-tester'
import type {
    DryRunResult,
    TierPolicy,
    TierPolicyDraftRequest,
    TierPolicyVersionsResponse,
} from '@/types/tier-policy'
import type {
    CreateMlroCaseRequest,
    CreateMlroCaseResponse,
    CreateMlroCaseWireResponse,
    MlroAlertListFilters,
    MlroAlertListResponse,
    MlroAlertListWire,
    MlroCase,
    MlroCaseAlertWireRequest,
    MlroCaseDetailWire,
    MlroCaseExportFormat,
    MlroCaseListFilters,
    MlroCaseListResponse,
    MlroCaseListWire,
    MlroCaseNoteRequest,
    MlroCaseNoteResponse,
    MlroCaseNoteWireResponse,
    MlroCaseStatusRequest,
    MlroCaseSummary,
    MlroCaseWire,
    MlroDashboardSummary,
} from '@/types/mlro'
import type {
    AccountClosureStatus,
    ClosureDismissResponse,
    ClosureExecuteResult,
    ClosureListResponse,
    ExtendKycDeadlineRequest,
    ExtendKycDeadlineResponse,
} from '@/types/closures'
import type {
    SybilClearRequest,
    SybilClearWalletResponse,
    SybilClearWalletWire,
    SybilClusterClearResponse,
    SybilClusterClearWire,
    SybilClusterDetail,
    SybilClusterDetailWire,
    SybilClusterListFilters,
    SybilClusterListResponse,
    SybilClusterListWire,
} from '@/types/sybil'
import {
    mapSybilClearResponse,
    mapSybilClusterClearResponse,
    mapSybilClusterDetail,
    mapSybilClusterList,
} from '@/lib/sybil-wire'
import {
    createCaseToWire,
    mapCreateCaseResponse,
    mapMlroAlertList,
    mapMlroCase,
    mapMlroCaseDetail,
    mapMlroCaseList,
    mapNoteResponse,
    noteToWire,
    severityToWire,
    statusToWire,
} from '@/lib/mlro-wire'

import type { ReferrersResponse } from '@/types/referrers'
import type {
    JurisdictionOverrideAddRequest,
    JurisdictionOverrideConflictCode,
    JurisdictionOverrideListWire,
    JurisdictionOverrideLiftRequest,
    JurisdictionOverrideWire,
} from '@/types/jurisdictions'
const OPERATOR_SESSION_KEY = 'opconsole.operatorSession'

export class ApiError extends Error {
    code: string
    status: number
    constructor(status: number, code: string, message: string) {
        super(message)
        this.name = 'ApiError'
        this.code = code
        this.status = status
    }
}

export interface StoredOperatorSession {
    token: string
    expiresAt: string
    chainIds: number[]
    address: string
    rolesByChain: RolesByChain
    roles: OperatorRole[]
}

export const operatorSession = {
    get(): StoredOperatorSession | null {
        const raw = sessionStorage.getItem(OPERATOR_SESSION_KEY)
        if (!raw) return null
        try {
            const parsed = JSON.parse(raw) as StoredOperatorSession
            if (!parsed.token || !parsed.expiresAt) return null
            if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
                sessionStorage.removeItem(OPERATOR_SESSION_KEY)
                return null
            }
            return { ...parsed, roles: parsed.roles ?? [], rolesByChain: parsed.rolesByChain ?? {} }
        } catch {
            sessionStorage.removeItem(OPERATOR_SESSION_KEY)
            return null
        }
    },
    set(session: StoredOperatorSession) {
        sessionStorage.setItem(OPERATOR_SESSION_KEY, JSON.stringify(session))
    },
    clear() {
        sessionStorage.removeItem(OPERATOR_SESSION_KEY)
    },
}

interface RequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
    body?: unknown
    signal?: AbortSignal
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const url = `${ENV.GAMING_SERVICE_URL.replace(/\/$/, '')}${path}`
    const headers: Record<string, string> = {
        Accept: 'application/json',
    }
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json'

    const session = operatorSession.get()
    if (session) headers['Authorization'] = `Bearer ${session.token}`

    const res = await fetch(url, {
        method: opts.method ?? 'GET',
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        signal: opts.signal,
    })

    if (res.status === 204) return undefined as T

    const text = await res.text()
    let data: unknown = undefined
    if (text) {
        try {
            data = JSON.parse(text)
        } catch {
            // fall through
        }
    }

    if (!res.ok) {
        // Two error shapes exist on the wire: the ErrorEnvelope
        // ({error:{code,message}}) and the closures/MLRO handlers' bare
        // {error:"message"} — accept both so the message survives.
        const env = data as ErrorEnvelope | { error?: string } | undefined
        const raw = env?.error
        const code = (typeof raw === 'object' && raw !== null ? raw.code : undefined) ?? 'INTERNAL'
        const message =
            typeof raw === 'string' && raw
                ? raw
                : ((typeof raw === 'object' && raw !== null ? raw.message : undefined) ??
                  `request failed with status ${res.status}`)
        throw new ApiError(res.status, code, message)
    }

    return data as T
}

// GET /readyz answers 503 whenever a chain is stale or erroring, and that body carries the
// per-chain detail an operator needs most, so a 503 is a degraded result, not an error. A failed
// db ping is a 503 with an ErrorEnvelope and no chains. Only a network error, a non-JSON body or
// any other status throws.
export async function fetchReadiness(): Promise<Readiness> {
    const res = await fetch(`${ENV.GAMING_SERVICE_URL.replace(/\/$/, '')}/readyz`, {
        headers: { Accept: 'application/json' },
    })
    if (res.status !== 200 && res.status !== 503) {
        throw new ApiError(res.status, 'INTERNAL', `readyz failed with status ${res.status}`)
    }
    let data: unknown
    try {
        data = JSON.parse(await res.text())
    } catch {
        throw new ApiError(res.status, 'INTERNAL', 'readyz returned a non-JSON body')
    }
    if (typeof data !== 'object' || data === null) {
        throw new ApiError(res.status, 'INTERNAL', 'readyz returned an unexpected body')
    }
    const body = data as Partial<ReadyResponse>
    return {
        degraded: res.status === 503,
        dbAvailable: body.db === 'ok',
        chains: body.chains ?? {},
    }
}

export function fetchOperation(id: string, signal?: AbortSignal): Promise<Operation> {
    return request<Operation>(`/v1/operations/${id}`, { signal })
}

export function requestOperatorNonce(body: NonceRequest): Promise<NonceResponse> {
    return request<NonceResponse>('/v1/auth/operator/nonce', { method: 'POST', body })
}

export function verifyOperatorSession(
    body: OperatorVerifyRequest,
): Promise<OperatorVerifyResponse> {
    return request<OperatorVerifyResponse>('/v1/auth/operator/verify', { method: 'POST', body })
}

export function fetchAuthMe(signal?: AbortSignal): Promise<AuthMeResponse> {
    return request<AuthMeResponse>('/v1/auth/me', { signal })
}

export function refreshAuthSession(): Promise<AuthRefreshResponse> {
    return request<AuthRefreshResponse>('/v1/auth/refresh', { method: 'POST' })
}

interface OperationsListQuery {
    limit?: number
    cursor?: OperationsListCursor
}

export function listOperations(query: OperationsListQuery = {}): Promise<OperationsListResponse> {
    const search = new URLSearchParams()
    if (query.limit !== undefined) search.set('limit', String(query.limit))
    if (query.cursor) {
        search.set('before', query.cursor.before)
        search.set('beforeId', query.cursor.beforeId)
    }
    const qs = search.toString()
    return request<OperationsListResponse>(`/v1/operations${qs ? `?${qs}` : ''}`)
}

// `chainId` scopes the lifetime figures and the first ledger page to one chain (BLO-990); the
// rest of the detail is person-scoped and the same on every chain.
export async function fetchPlayerDetail(
    address: string,
    chainId: number,
    signal?: AbortSignal,
): Promise<PlayerDetail> {
    const res = await request<PlayerDetailWire>(
        `/v1/players/${address}/detail?chainId=${chainId}`,
        { signal },
    )
    return normalizePlayerDetail(res, chainId)
}

// The service's JSON encoder omits empty slices, and partially-deployed
// versions of the endpoint may not yet return every field. Coalesce all
// list-like sub-resources to empty arrays so leaf components can rely on
// `.length` / `.map` without per-site guards.
export function normalizePlayerDetail(
    detail: Partial<PlayerDetailWire>,
    chainId: number,
): PlayerDetail {
    const lossLimits = detail.lossLimits
    return {
        player: detail.player as PlayerDetail['player'],
        attestation: detail.attestation ?? null,
        wallets: detail.wallets ?? [],
        walletOrigins: detail.walletOrigins ?? [],
        // Not coalesced: the service omits it for read-only callers (BLO-939), and [] would read
        // as "no findings".
        walletSybil: detail.walletSybil,
        gates: detail.gates ?? [],
        cumulative: detail.cumulative ?? ({} as PlayerDetail['cumulative']),
        sofRecords: detail.sofRecords ?? [],
        sowRecords: detail.sowRecords ?? [],
        documents: detail.documents ?? [],
        ledger: detail.ledger ?? { entries: [], nextCursor: null, chainId },
        tierHistory: detail.tierHistory ?? [],
        selfExclusion: detail.selfExclusion ?? null,
        lossLimits: {
            daily: lossLimits?.daily ?? null,
            weekly: lossLimits?.weekly ?? null,
            monthly: lossLimits?.monthly ?? null,
            pending: lossLimits?.pending ?? [],
        },
        openCases: detail.openCases ?? [],
        cachedAt: detail.cachedAt ?? '',
        cacheTtlSeconds: detail.cacheTtlSeconds ?? 0,
    }
}

interface PlayerLedgerQuery {
    cursor?: string
    limit?: number
}

export function fetchPlayerLedger(
    address: string,
    chainId: number,
    query: PlayerLedgerQuery = {},
    signal?: AbortSignal,
): Promise<LedgerPage> {
    const search = new URLSearchParams()
    search.set('chainId', String(chainId))
    if (query.cursor) search.set('cursor', query.cursor)
    if (query.limit !== undefined) search.set('limit', String(query.limit))
    return request<LedgerPage>(`/v1/players/${address}/ledger?${search.toString()}`, { signal })
}

export function fetchDocumentSignedUrl(
    documentId: string,
    signal?: AbortSignal,
): Promise<DocumentSignedUrlResponse> {
    return request<DocumentSignedUrlResponse>(`/v1/documents/${documentId}/signed-url`, { signal })
}

interface AuditLogListQuery extends AuditLogListFilters {
    cursor?: string
    limit?: number
}

export async function fetchAuditLog(
    query: AuditLogListQuery = {},
    signal?: AbortSignal,
): Promise<AuditLogListResponse> {
    const search = new URLSearchParams()
    if (query.actor) search.set('actor', query.actor)
    if (query.action) search.set('action', query.action)
    if (query.subjectType) search.set('subject_type', query.subjectType)
    if (query.subjectId) search.set('subject_id', query.subjectId)
    if (query.from) search.set('from', query.from)
    if (query.to) search.set('to', query.to)
    if (query.cursor) search.set('cursor', query.cursor)
    if (query.limit !== undefined) search.set('limit', String(query.limit))
    const qs = search.toString()
    const wire = await request<AuditLogListWire>(`/v1/audit-log${qs ? `?${qs}` : ''}`, {
        signal,
    })
    return {
        entries: (wire.entries ?? []).map(mapAuditEntry),
        nextCursor: wire.next_cursor || null,
    }
}

// The audit handler sends `audit.Record` as is: snake_case, empty fields omitted. `audit-wire.ts`
// maps it.
export async function fetchAuditEntry(id: string, signal?: AbortSignal): Promise<AuditEntry> {
    const wire = await request<AuditRecordWire>(`/v1/audit-log/${id}`, { signal })
    return mapAuditEntry(wire)
}

export function fetchTierPolicyVersions(signal?: AbortSignal): Promise<TierPolicyVersionsResponse> {
    return request<TierPolicyVersionsResponse>('/v1/tier-policy/versions', { signal })
}

export function fetchTierPolicy(policyId: string, signal?: AbortSignal): Promise<TierPolicy> {
    return request<TierPolicy>(`/v1/tier-policy/${policyId}`, { signal })
}

export function postTierPolicyDraft(
    body: TierPolicyDraftRequest,
): Promise<{ policyId: string; version: number }> {
    const path = '/v1/tier-policy/draft'
    return request(path, { method: 'POST', body })
}

export function postTierPolicyDryRun(policyId: string): Promise<DryRunResult> {
    const path = `/v1/tier-policy/${policyId}/dry-run`
    return request(path, { method: 'POST' })
}

export function postTierPolicyStage(
    policyId: string,
    body: { activatedAt: string },
): Promise<{ staged: true; policyId: string; activatedAt: string }> {
    const path = `/v1/tier-policy/${policyId}/stage`
    return request(path, { method: 'POST', body })
}

export function postTierPolicyCancelStage(
    policyId: string,
): Promise<{ cancelled: true; policyId: string }> {
    const path = `/v1/tier-policy/${policyId}/cancel-stage`
    return request(path, { method: 'POST' })
}

export function postTierPolicyRollback(
    policyId: string,
): Promise<{ policyId: string; version: number; activatedAt: string }> {
    const path = `/v1/tier-policy/${policyId}/rollback`
    return request(path, { method: 'POST' })
}

// Provisional jurisdiction blocks (BLO-848). Every console role may read; adding and lifting need
// MLRO, which the service also grants to admin. The three 409s come back as
// JurisdictionOverrideConflictError so the dialogs can say what went wrong.

const JURISDICTION_CONFLICT_CODES: ReadonlySet<string> = new Set<JurisdictionOverrideConflictCode>([
    'OVERRIDE_ACTIVE',
    'MATRIX_BLOCKED',
    'ALREADY_LIFTED',
])

export class JurisdictionOverrideConflictError extends ApiError {
    declare code: JurisdictionOverrideConflictCode
    constructor(code: JurisdictionOverrideConflictCode, message: string) {
        super(409, code, message)
        this.name = 'JurisdictionOverrideConflictError'
    }
}

async function withConflicts<T>(call: Promise<T>): Promise<T> {
    try {
        return await call
    } catch (err) {
        if (
            err instanceof ApiError &&
            err.status === 409 &&
            JURISDICTION_CONFLICT_CODES.has(err.code)
        ) {
            throw new JurisdictionOverrideConflictError(
                err.code as JurisdictionOverrideConflictCode,
                err.message,
            )
        }
        throw err
    }
}

export function fetchJurisdictionOverrides(
    signal?: AbortSignal,
): Promise<JurisdictionOverrideListWire> {
    return request<JurisdictionOverrideListWire>('/v1/jurisdiction-overrides', { signal })
}

export function postJurisdictionOverride(
    body: JurisdictionOverrideAddRequest,
): Promise<JurisdictionOverrideWire> {
    const path = '/v1/mlro/jurisdiction-overrides'
    return withConflicts(request<JurisdictionOverrideWire>(path, { method: 'POST', body }))
}

export function postJurisdictionOverrideLift(
    overrideId: string,
    body: JurisdictionOverrideLiftRequest,
): Promise<JurisdictionOverrideWire> {
    const path = `/v1/mlro/jurisdiction-overrides/${encodeURIComponent(overrideId)}/lift`
    return withConflicts(request<JurisdictionOverrideWire>(path, { method: 'POST', body }))
}

export type PlayerAction = 'suspend' | 'reinstate' | 'ban'

export function postPlayerAction(
    action: PlayerAction,
    address: string,
    body: { chainId: number },
): Promise<{ operationId: string }> {
    const path = `/v1/players/${address}/${action}`
    return request(path, { method: 'POST', body })
}

// Mirrors blockpot-service `mlroSelfExclusionRequest`: the player's UUID (`player.playerId` on the
// detail payload), not a wallet address. Anything that is not a UUID is a 400.
export interface MlroSelfExclusionRequest {
    player_id: string
    duration: SelfExclusionDuration
    reason: string
}

// Mirrors blockpot-service `selfExclusionRecord` as `MLROApply` returns it. Unlike the detail
// payload it is snake_case; `ends_at` is omitted for a permanent exclusion.
export interface MlroSelfExclusionResponse {
    id: string
    duration: SelfExclusionDuration
    starts_at: string
    ends_at?: string
    applied_by: 'player' | 'mlro'
    reason?: string
    lifted_at?: string
    lifted_by?: string
    lift_reason?: SelfExclusionLiftReason
    lift_note?: string
    is_active: boolean
}

export function postMlroSelfExclusion(
    body: MlroSelfExclusionRequest,
): Promise<MlroSelfExclusionResponse> {
    const path = '/v1/mlro/self-exclusion'
    return request(path, { method: 'POST', body })
}

export interface MlroLiftSelfExclusionRequest {
    reason: SelfExclusionLiftReason
    note: string
}

// The response body is not part of the BLO-930 contract; callers only rely on a 2xx.
export function postMlroLiftSelfExclusion(
    exclusionId: string,
    body: MlroLiftSelfExclusionRequest,
): Promise<unknown> {
    const path = `/v1/mlro/self-exclusion/${encodeURIComponent(exclusionId)}/lift`
    return request(path, { method: 'POST', body })
}

// MLRO player actions require the MLRO role on the service side; the console mirrors that
// gating via <RoleGate>. Each endpoint writes an `audit_log` row but none returns its id, so the
// toasts link to the player's audit log instead.

export interface OverrideTierRequest {
    tier: TierId
    reason: string
}

// blockpot-service `TierOverrideHandler.Set`: the override as written, with the acting operator's
// checksummed address. The audit row is written but its id is not returned.
export interface OverrideTierResponse {
    playerId: string
    tier: TierId
    setBy: string
}

export function postPlayerOverrideTier(
    address: string,
    body: OverrideTierRequest,
): Promise<OverrideTierResponse> {
    const path = `/v1/players/${address}/override-tier`
    return request(path, { method: 'POST', body })
}

export interface ClearSanctionsRequest {
    wallet: string
    reason: string
}

// blockpot-service `clearSanctionsResponse`. The audit row is written but its id is not returned.
export interface ClearSanctionsResponse {
    playerId: string
    wallet: string
    previousStatus: Extract<WalletSanctionsStatus, 'mlro_review' | 'sanctioned'>
    sanctionsStatus: Extract<WalletSanctionsStatus, 'false_positive_cleared'>
}

export function postPlayerClearSanctions(
    address: string,
    body: ClearSanctionsRequest,
): Promise<ClearSanctionsResponse> {
    const path = `/v1/players/${address}/clear-sanctions`
    return request(path, { method: 'POST', body })
}

export interface RescreenRequest {
    wallet?: string
}

// `sanctions.Status`, the screener's vocabulary: `hit` where `player_wallets` says `sanctioned`.
export type RescreenStatus = 'clear' | 'hit' | 'mlro_review' | 'error'

// blockpot-service `rescreenWalletResult`: snake_case keys; `block_number` and `detail` are
// omitempty, and `source` is empty when the screener failed.
export interface RescreenWalletResult {
    wallet: string
    status: RescreenStatus
    source: string
    block_number?: number
    checked_at: string
    detail?: string
}

// blockpot-service `rescreenResponse`: one result per screened wallet, no audit entry id.
export interface RescreenResponse {
    playerId: string
    results: RescreenWalletResult[]
}

export function postPlayerRescreen(
    address: string,
    body: RescreenRequest,
): Promise<RescreenResponse> {
    const path = `/v1/players/${address}/rescreen`
    return request(path, { method: 'POST', body })
}

// blockpot-service `revokeGateRequest`: the gate key is snake_case on the wire.
export interface RevokeGateRequest {
    gate_type: GateType
    reason: string
}

// blockpot-service `revokeGateResponse`, sent with 200. The gate row is revoked before the handler
// answers; the on-chain bit-clear is enqueued behind it. `newTier` is omitempty: it is the chain
// tier read back at response time, absent when the reader has no answer.
export interface RevokeGateResponse {
    playerId: string
    gate_type: GateType
    status: 'revoked'
    newTier?: TierId
}

export function postPlayerRevokeGate(
    address: string,
    body: RevokeGateRequest,
): Promise<RevokeGateResponse> {
    const path = `/v1/players/${address}/revoke-gate`
    return request(path, { method: 'POST', body })
}

// Records an audit-log row for an operator-wallet-initiated
// `setLifetimeEurAdjustment` on-chain write. The on-chain tx is signed by the
// operator's wallet (not relayed by the service), so the console reports the
// confirmed tx hash and adjustment metadata back to the service so the row
// lands in `audit_log` alongside the other MLRO actions.
export interface AdjustLifetimeEurRequest {
    chainId: number
    enteredDeltaEurMinor: string
    wonDeltaEurMinor: string
    ethUsd8: string
    eurUsd8: string
    atBlock: string
    idempotencyKey: `0x${string}`
    txHash: `0x${string}`
    reason: string
}

// blockpot-service `adjustLifetimeEurResponse`: `auditEntryId` is always the empty string. The
// handler writes the audit row but, like the other MLRO writes, does not return its id.
export interface AdjustLifetimeEurResponse {
    auditEntryId: ''
}

export function postPlayerAdjustLifetimeEur(
    address: string,
    body: AdjustLifetimeEurRequest,
): Promise<AdjustLifetimeEurResponse> {
    const path = `/v1/players/${address}/adjust-lifetime-eur`
    return request(path, { method: 'POST', body })
}

export function fetchMlroDashboard(signal?: AbortSignal): Promise<MlroDashboardSummary> {
    return request<MlroDashboardSummary>('/v1/mlro/dashboard', { signal })
}

interface MlroCaseListQuery extends MlroCaseListFilters {
    cursor?: string
    limit?: number
}

// The cases handler speaks snake_case in both directions; `mlro-wire.ts` maps it (BLO-946).
export async function fetchMlroCases(
    query: MlroCaseListQuery = {},
    signal?: AbortSignal,
): Promise<MlroCaseListResponse> {
    const search = new URLSearchParams()
    if (query.status) search.set('status', query.status)
    if (query.severity) search.set('severity', severityToWire(query.severity))
    if (query.subjectType) search.set('subject_type', query.subjectType)
    if (query.subjectId) search.set('subject_id', query.subjectId)
    if (query.cursor) search.set('cursor', query.cursor)
    if (query.limit !== undefined) search.set('limit', String(query.limit))
    const qs = search.toString()
    const wire = await request<MlroCaseListWire>(`/v1/mlro/cases${qs ? `?${qs}` : ''}`, { signal })
    return mapMlroCaseList(wire)
}

export async function fetchMlroCase(caseId: string, signal?: AbortSignal): Promise<MlroCase> {
    const wire = await request<MlroCaseDetailWire>(`/v1/mlro/cases/${caseId}`, { signal })
    return mapMlroCaseDetail(wire)
}

export async function postMlroCase(body: CreateMlroCaseRequest): Promise<CreateMlroCaseResponse> {
    const wire = await request<CreateMlroCaseWireResponse>('/v1/mlro/cases', {
        method: 'POST',
        body: createCaseToWire(body),
    })
    return mapCreateCaseResponse(wire)
}

export async function postMlroCaseNote(
    caseId: string,
    body: MlroCaseNoteRequest,
): Promise<MlroCaseNoteResponse> {
    const path = `/v1/mlro/cases/${caseId}/notes`
    const wire = await request<MlroCaseNoteWireResponse>(path, {
        method: 'POST',
        body: noteToWire(body),
    })
    return mapNoteResponse(wire)
}

// Answers with the updated case.
export async function postMlroCaseStatus(
    caseId: string,
    body: MlroCaseStatusRequest,
): Promise<MlroCaseSummary> {
    const path = `/v1/mlro/cases/${caseId}/status`
    const wire = await request<MlroCaseWire>(path, { method: 'POST', body: statusToWire(body) })
    return mapMlroCase(wire)
}

// Answers 204.
export async function postMlroCaseDetachAlert(caseId: string, alertId: string): Promise<void> {
    const path = `/v1/mlro/cases/${caseId}/detach-alert`
    const body: MlroCaseAlertWireRequest = { alert_id: alertId }
    await request<void>(path, { method: 'POST', body })
}

// Export streams a JSON blob; we want the raw bytes (not the parsed object)
// so the caller can hand them to a Blob + download anchor.
export async function fetchMlroCaseExport(
    caseId: string,
    format: MlroCaseExportFormat,
): Promise<Blob> {
    const url = `${ENV.GAMING_SERVICE_URL.replace(/\/$/, '')}/v1/mlro/cases/${caseId}/export?format=${format}`
    const headers: Record<string, string> = { Accept: 'application/json' }
    const session = operatorSession.get()
    if (session) headers['Authorization'] = `Bearer ${session.token}`
    const res = await fetch(url, { headers })
    if (!res.ok) {
        const text = await res.text()
        let envelope: ErrorEnvelope | undefined
        try {
            envelope = text ? (JSON.parse(text) as ErrorEnvelope) : undefined
        } catch {
            // fall through
        }
        const code = envelope?.error?.code ?? 'INTERNAL'
        const message = envelope?.error?.message ?? `request failed with status ${res.status}`
        throw new ApiError(res.status, code, message)
    }
    return res.blob()
}

interface MlroAlertListQuery extends MlroAlertListFilters {
    cursor?: string
    limit?: number
}

export async function fetchMlroAlerts(
    query: MlroAlertListQuery = {},
    signal?: AbortSignal,
): Promise<MlroAlertListResponse> {
    const search = new URLSearchParams()
    if (query.category) search.set('category', query.category)
    if (query.severity) search.set('severity', query.severity)
    if (query.acknowledged) search.set('acknowledged', query.acknowledged)
    if (query.cursor) search.set('cursor', query.cursor)
    if (query.limit !== undefined) search.set('limit', String(query.limit))
    const qs = search.toString()
    const wire = await request<MlroAlertListWire>(`/v1/mlro/alerts${qs ? `?${qs}` : ''}`, {
        signal,
    })
    return mapMlroAlertList(wire)
}

// Answers 204.
export async function postMlroAlertAck(alertId: string): Promise<void> {
    await request<void>(`/v1/mlro/alerts/${alertId}/ack`, { method: 'POST' })
}

interface SybilClusterListQuery extends SybilClusterListFilters {
    cursor?: string
    limit?: number
}

// The Sybil handler speaks snake_case; `sybil-wire.ts` maps it (BLO-937).
export async function fetchSybilClusters(
    query: SybilClusterListQuery = {},
    signal?: AbortSignal,
): Promise<SybilClusterListResponse> {
    const search = new URLSearchParams()
    if (query.status) search.set('status', query.status)
    if (query.cursor) search.set('cursor', query.cursor)
    if (query.limit !== undefined) search.set('limit', String(query.limit))
    const qs = search.toString()
    const wire = await request<SybilClusterListWire>(`/v1/sybil/clusters${qs ? `?${qs}` : ''}`, {
        signal,
    })
    return mapSybilClusterList(wire)
}

export async function fetchSybilCluster(
    clusterId: string,
    signal?: AbortSignal,
): Promise<SybilClusterDetail> {
    const wire = await request<SybilClusterDetailWire>(`/v1/sybil/clusters/${clusterId}`, {
        signal,
    })
    return mapSybilClusterDetail(wire)
}

export async function postPlayerSybilClear(
    address: string,
    body: SybilClearRequest,
): Promise<SybilClearWalletResponse> {
    const path = `/v1/players/${address}/sybil-clear`
    const wire = await request<SybilClearWalletWire>(path, { method: 'POST', body })
    return mapSybilClearResponse(wire)
}

// Clears every wallet in the cluster still awaiting a clear. 400 `SYBIL_CLEAR_INVALID` for a bad
// reason or short note, 409 `SYBIL_ALREADY_CLEARED` when nothing is left to clear.
export async function postSybilClusterClear(
    clusterId: string,
    body: SybilClearRequest,
): Promise<SybilClusterClearResponse> {
    const path = `/v1/sybil/clusters/${clusterId}/clear`
    const wire = await request<SybilClusterClearWire>(path, { method: 'POST', body })
    return mapSybilClusterClearResponse(wire)
}

export interface EntryVolumeQuery {
    from: string
    to: string
    granularity: EntryVolumeGranularity
    draw: string // 0x-prefixed lower-case address or 'all'
    currency: EntryVolumeCurrency
}

// The totals and buckets cover one chain, which the response echoes (BLO-990).
export function fetchEntryVolume(
    query: EntryVolumeQuery & { chainId: number },
    signal?: AbortSignal,
): Promise<EntryVolumeResponse> {
    const search = new URLSearchParams()
    search.set('chainId', String(query.chainId))
    search.set('from', query.from)
    search.set('to', query.to)
    search.set('granularity', query.granularity)
    search.set('draw', query.draw)
    search.set('currency', query.currency)
    return request<EntryVolumeResponse>(`/v1/operator/entry-volume?${search.toString()}`, {
        signal,
    })
}

// The execute endpoint is synchronous server-side (ban + forfeit receipt
// waits, up to ~3 minutes worst case); give the client a little headroom.
const CLOSURE_EXECUTE_TIMEOUT_MS = 240_000

export function fetchMlroClosures(
    status?: AccountClosureStatus,
    signal?: AbortSignal,
): Promise<ClosureListResponse> {
    const qs = status ? `?status=${status}` : ''
    return request<ClosureListResponse>(`/v1/mlro/closures${qs}`, { signal })
}

export function postMlroClosureExecute(closureId: string): Promise<ClosureExecuteResult> {
    return request<ClosureExecuteResult>(`/v1/mlro/closures/${closureId}/execute`, {
        method: 'POST',
        signal: AbortSignal.timeout(CLOSURE_EXECUTE_TIMEOUT_MS),
    })
}

export function postMlroClosureDismiss(
    closureId: string,
    note: string,
): Promise<ClosureDismissResponse> {
    return request<ClosureDismissResponse>(`/v1/mlro/closures/${closureId}/dismiss`, {
        method: 'POST',
        body: { note },
    })
}

export function postExtendKycDeadline(
    address: string,
    body: ExtendKycDeadlineRequest,
): Promise<ExtendKycDeadlineResponse> {
    return request<ExtendKycDeadlineResponse>(`/v1/mlro/players/${address}/extend-kyc-deadline`, {
        method: 'POST',
        body,
    })
}

// The referrer set for the roster and the registration preflight. Operator JWT
// with admin, mlro or read_only on the chain.
export function fetchReferrers(chainId: number, signal?: AbortSignal): Promise<ReferrersResponse> {
    return request<ReferrersResponse>(`/v1/referrers?chainId=${chainId}`, { signal })
}

// The beta-tester whitelist that gates the in-game problem reporter (BLO-999). Person-scoped: no
// chainId. GET for every console role, PUT for admin only; 404 when the address has no player.
export function fetchBetaTester(address: string, signal?: AbortSignal): Promise<BetaTester> {
    return request<BetaTester>(`/v1/players/${address}/beta-tester`, { signal })
}

export function putBetaTester(address: string, body: BetaTesterUpdate): Promise<BetaTester> {
    return request<BetaTester>(`/v1/players/${address}/beta-tester`, { method: 'PUT', body })
}
